import { useEffect, useRef } from 'react'
import PlusIcon from '../../../assets/svgs/Plus'
import SendIcon from '../../../assets/svgs/Send'
import { useFilesContext } from '../../../contexts/FilesContext'
import { logEvent } from '../../../utils/logEvent'
import style from './ChatInput.module.css'

const ChatInput = ({
  openUpload,
  setOpenUpload,
  handleResponses,
  setFormData,
  formData = { message: '' },
  errorMessage,
  setErrorMessage,

  // Tool props
  activeTool,                // 'jobFitCheck' | null
  setActiveTool,             // setter
  jobToolDisabled = false,   // disabled when any item is attached
}) => {
  const inputRef = useRef(null)
  const { files } = useFilesContext()

  useEffect(() => {
    if (errorMessage && inputRef.current) {
      inputRef.current.focus()
    }
  }, [errorMessage])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (errorMessage) {
      setErrorMessage('')
    }
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!errorMessage && formData.message) {
      logEvent({
        action: 'message_chat',
        content: formData.message,
      })
    }

    handleResponses(formData.message)
    setFormData({ message: '' })
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit(e)
  }

  // Toggle tools dropdown
  const handleOpen = () => {
    setOpenUpload(!openUpload)
  }

  const toggleJobFitTool = () => {
    if (jobToolDisabled) return
    const next = activeTool === 'jobFitCheck' ? null : 'jobFitCheck'
    setActiveTool(next)
    setOpenUpload(false)
    logEvent({
      action: next ? 'tool_enabled' : 'tool_disabled',
      content: 'Job Fit Check',
    })
  }

  return (
    <div className={style.chatBigInputContainer} style={{ position: 'relative' }}>
      {/* Tools toggle (always visible, before the message input) */}
      <div style={{ position: 'relative', marginRight: 8 }}>
        <button
          type="button"
          onClick={handleOpen}
          aria-label="Open tools"
          aria-haspopup="menu"
          aria-expanded={openUpload ? 'true' : 'false'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 999,
            border: '1px solid var(--border, #e5e7eb)',
            background: 'white',
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <PlusIcon />
        </button>

        {/* Tools dropdown (anchored ABOVE the + button, transparent bg, stronger shadow) */}
        {openUpload && (
          <div
            role="menu"
            aria-label="Tools"
            style={{
              position: 'absolute',
              bottom: '110%', // above the button
              left: 0,
              minWidth: 220,
              background: 'transparent',
              border: '1px solid var(--border, #e5e7eb)',
              borderRadius: 12,
              boxShadow: '0 12px 32px rgba(0,0,0,0.20), 0 3px 10px rgba(0,0,0,0.18)',
              padding: 8,
              zIndex: 9999,
            }}
          >
            <button
              role="menuitem"
              onClick={toggleJobFitTool}
              disabled={jobToolDisabled}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: 'none',
                cursor: jobToolDisabled ? 'not-allowed' : 'pointer',
                opacity: jobToolDisabled ? 0.5 : 1,
                background: 'transparent',
              }}
              title={
                jobToolDisabled
                  ? 'Disabled: an item is already attached'
                  : 'Toggle Job Fit Check'
              }
            >
              <span>Job Fit Check</span>
              <span
                style={{
                  fontSize: 12,
                  padding: '2px 6px',
                  borderRadius: 999,
                  border: '1px solid var(--border, #e5e7eb)',
                  background: 'transparent',
                }}
              >
                {activeTool === 'jobFitCheck' ? 'On' : 'Off'}
              </span>
            </button>
          </div>
        )}
      </div>

      <div
        className={`${style.chatInputWrapper} ${errorMessage ? style.chatInputWrapper_error : ''}`}
      >
        <input
          ref={inputRef}
          className={style.chatBiginput}
          placeholder="Ask Chimp"
          value={formData.message || ''}
          name="message"
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />

        {files.length > 0 && (
          <div className={style.chatFilesCounter}>
            <span>{files.length}</span>
          </div>
        )}
      </div>

      <button className={style.send} onClick={handleSubmit}>
        <SendIcon />
      </button>
    </div>
  )
}

export default ChatInput
